/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import browserDetection from 'js-sdk/src/common/util/browserDetection';
import fontUtils from 'js-sdk/src/common/util/fontUtils';
import datasetTypes from '../../enums/datasetTypes';
import GaugeGenericChartVisualization from './chart/gauge/GaugeGenericChartVisualization';
import gaugeChartUtilities from './chart/gauge/utilities';
import getUserSettings from './chart/gauge/getUserSettings';
// @ts-ignore
import defaultPalette from './chart/palette/defaultPalette';
import genericChartVisualizationStaticPropertiesReducer from './chart/reducers/genericChartVisualizationReducer';
import { getEvents } from './chart/util/EventUtil';

const RATIO_HORIZONTAL_WHOLE_UNITS = 10;
const RATIO_VERTICAL_FIELDS_UNITS = 2;
const RATIO_VERTICAL_CHART_UNITS = 10;

// CIRCLE_CHART_SIZE is how much available space the will the circle of the chart take from the whole area for one chart
const CIRCLE_CHART_SIZE = 0.90; // in percents, it's 90%

// this variable specifies how thick the circle would be compared to the area for one chart
const CIRCLE_CHART_GAGE = 0.12; // in percents, it's 12%

const calculateSizes = (props :any) => {

    const {
        chartAreaSize,
        containerWidth,
        containerHeight
    } = props;

    const minimalContainerSideLength :number = Math.min(containerWidth, containerHeight);

    // we don't let the circle to take whole chart area, we want it be smaller to have some space
    // between one chart and the next one
    const circleDiameter :number = chartAreaSize.byHorizontal * CIRCLE_CHART_SIZE;

    // Above the chart there will be a some text (field names), so let's find it's height:
    let heightAvailableForFields :number = (chartAreaSize.byVertical / (RATIO_VERTICAL_FIELDS_UNITS + RATIO_VERTICAL_CHART_UNITS)) * RATIO_VERTICAL_FIELDS_UNITS;
    // also give to fields a space above the circle which we don't use because
    heightAvailableForFields += chartAreaSize.byHorizontal * (1 - CIRCLE_CHART_SIZE);

    // And we have to free that space by moving center of the chart down by half of that reserved size.
    // If it seems counter-intuitive you may draw a rectangle, find it's center, then cut some space at the top, and
    // find the center of space which left. You'll see that center was moved by half of the removed space.
    const verticalCenterOffset :number = heightAvailableForFields / 2;

    // The next line might seems weird but it's actually based on HC's code and our offsets which we give to HC.
    // Here is the logic behind how HC finds the vertical position for the title (HC calls it yTitle):
    // titleYPosition = yPositionOfTheChartCenter - smallestSideOfTheContainer / 2 + usersOffset - HC.fontBaseline(fontSize)
    //               (aka center[1] from HC source)  (aka center[2] from HC source)
    // You may ask question: why it uses the minimum side of the container ?
    // Well, you need to understand what we are hacking HC, it's logic indented to render only one chart on a plot.
    // To get proper position of the title we need to remove 'smallestSideOfTheContainer / 2' and HC.fontBaseline(fontSize)
    // form the equation by adding these values to 'usersOffset'.
    // The smallest side is available here, but the font size is unknown yet. We'll add it later once we figure out the
    // font size.
    // After all of this we'll get to the center of the chart.
    // So we need to get higher to the top of the circle. We can do this by using 'chartHorizontalSize / 2' which is the
    // radius of the circle.
    const fieldVerticalOffset :number = minimalContainerSideLength / 2 - circleDiameter / 2;

    // the radius values given in percents
    // and HC converts all percents to numbers by using the smallest container side, so do we
    const outerRadius :number = (100 * circleDiameter) / minimalContainerSideLength;
    // the inner radius is smaller:
    const innerRadius :number = outerRadius * (1 - CIRCLE_CHART_GAGE);

    return {
        circleDiameter,
        heightAvailableForFields,
        verticalCenterOffset,
        fieldVerticalOffset,
        outerRadius,
        innerRadius
    };
};

const calculateFontData = (props :any) => {

    const {
        sizes,
        chartsData,
        formattingOptions,
        chartUserSettings
    } = props;

    const bigFontSize :number = 20;
    const smallFontSize :number = 16;
    const minimalFontSize :number = 2;

    // in pixels
    const widthAvailableForOneField :number = Math.floor(sizes.circleDiameter);

    const widthAvailableForValueText :number = Math.floor(sizes.circleDiameter * ((1 - CIRCLE_CHART_GAGE) - 0.2));
    const heightAvailableForValueText :number = Math.floor(sizes.circleDiameter * 0.3);

    const widthAvailableForMeasure :number = Math.floor(sizes.circleDiameter * ((1 - CIRCLE_CHART_GAGE) - 0.2));
    const heightAvailableForMeasure :number = Math.floor(sizes.circleDiameter * 0.1);

    const texts :any[] = [];
    const amountOfFieldLinesPerChart :any = [];
    chartsData.forEach((chartData :any) => {

        let theLongestField :string = '';
        chartData.fieldNames.forEach((fieldName :string) => {
            if (fieldName && fieldName.length > theLongestField.length) {
                theLongestField = fieldName;
            }
        });
        amountOfFieldLinesPerChart.push(chartData.fieldNames.length);

        texts.push({
            field: theLongestField,
            value: chartData.valueAsString + chartUserSettings.valueSuffix,
            measure: chartData.measureName
        })
    });
    const maximalAmountOfFieldLines :number = Math.max(...amountOfFieldLinesPerChart);
    const heightAvailableForOneField :number = Math.floor(sizes.heightAvailableForFields / maximalAmountOfFieldLines);

    const fontData = gaugeChartUtilities.calculateFontData({
        texts,
        formattingOptions,
        autoScaleStrategy: 'minimalFontForAll',
        fonts: {
            fieldFontSize: smallFontSize,
            valueFontSize: bigFontSize,
            measureFontSize: smallFontSize,
            minimalFontSize
        },
        constrains: {
            field: {
                width: widthAvailableForOneField,
                height: heightAvailableForOneField
            },
            value: {
                width: widthAvailableForValueText,
                height: heightAvailableForValueText
            },
            measure: {
                width: widthAvailableForMeasure,
                height: heightAvailableForMeasure,
            }
        }
    });

    // Now, we need to find out the vertical offset for Value and Measure texts.
    // By default HC renders them incorrectly, it might be changed with new HC versions, but for now we need to patch
    // the position.
    // If you ever encounter some issue with positioning and you'd like to debug, you may use the HC debug patch from
    // https://jira.tibco.com/browse/JS-36758
    // Just try to apply it to HC source code and the rest would be easy.

    // How HC renders these labels ? Well, it creates the <g> element, inside of it a <text> element
    // and there it creates two <tspan>, one for Value and second for Measure.
    // Next, it takes the font size of <text> (yes, you read it right, from <text>), calculates the font base line and
    // assigns this value to a 'y' attribute of the first <tspan>.
    // Next, it takes the font size of the second <tspan>, calculates a font line height and assigns this value to
    // the a 'dy' attribute of the second <tspan>.
    // By this HC avoid text overlapping because by default SVG 'renders' all elements in 0,0 position.
    // Next, HC need to position this <text> element with two <tspan> inside a corresponding gauge circle, and it done by
    // taking the gauge circle point.
    // Next it asks the browser what size of the <text> element. And you may be surprised by that size the browser
    // returns because... just check it out and compare to the font size of Value and Measure. Sometimes its height
    // it bigger than the sum on font size of Value and Measure, sometimes it's smaller. There is the reason behind
    // this: different font metrics. Not each user has all fonts installed and sometimes we use quite weird fonts,
    // like the 'Lucida Sans Unicode' for instance.
    // Check out next link to get it better: https://iamvdo.me/en/blog/css-font-metrics-line-height-and-vertical-align
    // So, by having the center point of gauge circle and height of the <text> element HC can finally position the Value
    // and Measure above the gauge circle. the formula is simple:
    //      <g>_y_translate = CenterPoint_y_position - TextElement_height
    // As you see <g> element is moved by translate() command.

    // As I mentioned above, the size of the <text> returned by browser may be bigger or smaller the actual size, and
    // the Value and Measure may get wrong vertical position.
    // To fix it we calculate the same size of the <text> by calling getSVGTextRect() method and passing to it the same
    // font specifications and text, and calculate the correct vertical offset which would be ideal.
    // All the code which does it goes below.
    // The only exception is a browser support of <text> node: the FireFox, IE11 and Ege does not support getting font
    // size assigned to style attribute:
    // textNode.style.fontSize would return '50px' in Chrome and empty string in rest browsers.
    // So, the HC code assumes the font size of 12px which is a fallback.
    // I think HC would fix this error later and if you notice that after HC update the Firefox renders Value and
    // Measure in a wrong position then check: either FF added support or HC fixed its code.

    chartsData.forEach((chartData :any, paneIndex :number) => {

        const fontInfo :any = fontData[paneIndex];
        const {
            value,
            measure
        } = texts[paneIndex];

        // we mimic the same action which HC does (read the text above to understand why it happens)
        let valueVerticalOffset :number = fontUtils.getFontBaseline(fontInfo.value.fontSize);
        if (browserDetection.isFirefox() || browserDetection.isIE11() || browserDetection.isEdge()) {
            // and this is a fallback
            valueVerticalOffset = 12;
        }

        const linesInTextNode :any = [
            {
                text: value,
                fontSize: fontInfo.value.fontSize,
                sizeUnits: 'px',
                fontWeight: 'bold',
                lineHeight: 'normal',
                y: valueVerticalOffset
            }
        ];

        if (chartData.measureName) {
            linesInTextNode.push({
                text: measure,
                fontSize: fontInfo.measure.fontSize,
                sizeUnits: 'px',
                fontWeight: 'normal',
                lineHeight: 'normal',
                y: fontUtils.getFontHeight(fontInfo.measure.fontSize)
            });
        }

        let correctOffset :number = 0;
        linesInTextNode.forEach((line :any) => {
            correctOffset += line.y;
        });
        // we divide by 2 the height of Value and Measure because we'd like to shift them just by half, want to place
        // them in the middle
        correctOffset = Math.round(correctOffset / 2);

        const textRect :any = fontUtils.getSVGTextRect(linesInTextNode);

        // 'measureAndValueYOffset' is our offset to Value and Measure which Hc would take into account.
        // In the formula listen above we'd like to ger rid of 'TextElement_height' and replace it with correct offset.
        // The actual formula is:
        //     <g>_y_translate = CenterPoint_y_position - TextElement_height + correctionFromUser
        // Of course, -1 must be applied because HC adds our correction.
        // So in the final we'd have next:
        //     <g>_y_translate = CenterPoint_y_position - correctOffset

        // You may wonder why the 'correctOffset' is based on getFontBaseline() and getFontHeight().
        // Well, it's because they are applied to <tspan> elements and if you try to set them to 0 you'll see how
        // the Value and Measure would be rendered just on the top line of <text> boundary. This is how SVG works.
        // And I just don't mind to use the offsets given to <tspan> elements by HC.

        fontInfo.measureAndValueYOffset = -1 * (correctOffset - textRect.height);
    });

    return fontData;
};

export class GaugeVisualization extends GaugeGenericChartVisualization {

    getChartSection() {
        const chart :any = super.getChartSection();
        return {
            ...chart,
            type: 'solidgauge',
            margin: [0, 0, 0, 0],
            spacing: [0, 0, 0, 0]
        };
    }

    generateHighchartsConfig(props :any) {

        const {
            sizes,
            chartsData,
            fontData,
            chartPositions,
            linkOptions,
            formattingOptions,
            chartUserSettings
        } = props;

        const generalConfig :any = {};
        const plotOptionsDefaults :any = this.getPlotOptionsSection();
        const seriesColors :any = formattingOptions.seriesColors || [];
        const events :any = getEvents(linkOptions);
        const haveEvents :boolean = Object.keys(events).length > 0;

        const gaugeChartConfig = {
            ...generalConfig,
            chart: this.getChartSection(),
            credits: {
                enabled: false
            },
            legend: {},
            plotOptions: {
                ...plotOptionsDefaults,
                solidgauge: {
                    // stickyTracking is important, don't remove it
                    stickyTracking: false
                }
            },
            tooltip: {
                enabled: false
            },
            time: {
                useUTC: true
            },
            title: {
                text: null
            },
            pane: [],
            xAxis: [],
            yAxis: [],
            series: [],

            // The next variable is the internal AIC variable used to get proper reaction on resize events of the container.
            // Specifies how much the container can be resized (expanded / collapsed) before we need to re-draw the chart.
            // in pixels.
            resizeThreshold: 20
        };

        chartsData.forEach((chartData :any, paneIndex :number) => {

            const xCenter :number = chartPositions[paneIndex].xCenterInPercent;
            const yCenter :number = chartPositions[paneIndex].yCenterInPercent;
            const fontInfo :any = fontData[paneIndex];

            gaugeChartConfig.pane.push({
                size: '100%',
                background: {
                    backgroundColor: '#eeeeee',
                    borderWidth: 0,
                    shape: 'arc',
                    innerRadius: `${sizes.innerRadius}%`,
                    outerRadius: `${sizes.outerRadius}%`,
                },
                center: [`${xCenter}%`, `${yCenter}%`]
            });

            gaugeChartConfig.xAxis.push({
                pane: paneIndex
            });

            // The next section may seem complicated. It's HC's logic, don't ask me why it works in this way :-)
            // So, sizes.fieldVerticalOffset is the offset for the fields based on an circle diameter, and it doesn't rely
            // on a font size. To find the real offset for field names we need to follow the logic which explained
            // in function calculateSizes()
            let yTitleOffset :number = sizes.fieldVerticalOffset + fontUtils.getFontBaseline(fontInfo.field.fontSize);

            // Now we need to "move" the fields to the top by the size of height which HC will add for each field line.
            // HC does this by taking font height and we need to do the same:
            yTitleOffset -= fontUtils.getFontHeight(fontInfo.field.fontSize) * chartData.fieldNames.length;

            // and not just round the value
            yTitleOffset = Math.round(yTitleOffset);

            gaugeChartConfig.yAxis.push({
                min: chartUserSettings.minValue,
                max: chartUserSettings.maxValue,
                stops: chartUserSettings.colorStops,
                lineWidth: 0,
                minorTickInterval: 0,
                tickPositions: [chartUserSettings.minValue, chartUserSettings.maxValue],
                tickLength: 0,
                tickWidth: 0,
                labels: {
                    enabled: false
                },
                pane: paneIndex,
                title: {
                    useHTML: false,
                    align: 'high',
                    textAlign: 'center',
                    y: yTitleOffset,
                    text: chartData.fieldNames.join('<br>'),
                    style: {
                        color: fontInfo.field.color,
                        'text-align': 'center',
                        'font-size': `${fontInfo.field.fontSize}px`
                    }
                }
            });

            const defaultColor :string = defaultPalette.colors[paneIndex % defaultPalette.colors.length];
            const serieColor :string = seriesColors[paneIndex] || defaultColor;

            let dataLabelText :string = `<span style="font-size:${fontInfo.value.fontSize}px;color:'${fontInfo.value.color}'">${chartData.valueAsString}${chartUserSettings.valueSuffix}</span>`;
            if (chartData.measureName) {
                dataLabelText += `
                <br/>
                <span style="font-weight:normal;font-size:${fontInfo.measure.fontSize}px;color:'${fontInfo.measure.color}'">${chartData.measureName}</span>
            `;
            }

            // remove all spaces at the beginning and in the end + remove spaces between tags
            dataLabelText = dataLabelText.trim().replace(new RegExp('>[\\s]+<', 'g'), '><');

            gaugeChartConfig.series.push({
                yAxis: paneIndex,
                xAxis: paneIndex,
                // this 'color' key doesn't work because it's ignored by HC because we have set 'stops' on yAxis.
                // HC uses stop colors instead of defined color here
                // But I decided to left it here because it may be extra level of redundancy
                color: serieColor,
                cursor: haveEvents ? 'pointer' : 'default',
                innerRadius: `${sizes.innerRadius}%`,
                radius: `${sizes.outerRadius}%`,
                name: chartData.measureName,
                data: [{
                    y: chartData.value,
                    linkOutputParams: chartData.linkOutputParams
                }],
                point: {
                    events
                },
                dataLabels: {
                    useHTML: false,
                    allowOverlap: true,
                    overflow: 'allow',
                    verticalAlign: 'bottom',
                    style: {
                        // here we specify the font size of the upper text which is the value
                        fontSize: fontInfo.value.fontSize,
                        'text-anchor': 'middle'
                    },
                    align: 'left',
                    x: 0,
                    y: fontInfo.measureAndValueYOffset,
                    padding: 0,
                    borderWidth: 0,
                    formatter: () => {
                        return dataLabelText;
                    }
                }
            });
        });

        return gaugeChartConfig;
    }

    getHighchartsOptions() {
        const formattingOptions :any = this.getFormattingOptions();
        const chartUserSettings :any = getUserSettings(formattingOptions);
        const containerWidth :number = this.getContainerWidth();
        const containerHeight :number = this.getContainerHeight();
        const linkOptions :any = this.getLinkOptions();

        const chartsData :any = this.collectDataForCharts(chartUserSettings);

        const amountOfCharts :number = chartsData.length;

        const chartAreaSize :any = gaugeChartUtilities.getChartAreaSize({
            aspectRation: RATIO_HORIZONTAL_WHOLE_UNITS / (RATIO_VERTICAL_FIELDS_UNITS + RATIO_VERTICAL_CHART_UNITS),
            amountOfCharts,
            containerWidth,
            containerHeight,
            chartUserSettings
        });

        const sizes :any = calculateSizes({
            chartAreaSize,
            containerWidth,
            containerHeight
        });

        const fontData :any = calculateFontData({
            sizes,
            chartAreaSize,
            chartsData,
            formattingOptions,
            chartUserSettings
        });

        const chartPositions :any = gaugeChartUtilities.getGaugeChartPositions({
            sizes,
            amountOfCharts,
            chartAreaSize,
            containerWidth,
            containerHeight,
            chartUserSettings
        });

        let highchartsOptions :any = this.generateHighchartsConfig({
            sizes,
            chartsData,
            fontData,
            chartAreaSize,
            chartPositions,
            linkOptions,
            formattingOptions,
            chartUserSettings
        });

        highchartsOptions = super.finalOptionAdjustment(highchartsOptions);

        super.findAndRecordMinMaxValues(highchartsOptions);

        return highchartsOptions;
    }
}

export const GaugeVisualizationDescriptor = {
    legacyAdhoc: 'gauge',
    parentClass: 'Gauge',
    cssClass: 'Gauge',
    bundleName: 'adhoc.visualisation.chooser.type.GAUGE',
    name: 'Gauge',
    group: 'adhoc.visualisation.chooser.type.GROUP_GAUGE',
    datasetType: datasetTypes.MULTI_AXIS,
    component: GaugeVisualization,
    staticPropertiesReducer: genericChartVisualizationStaticPropertiesReducer,
    requirements: {
        measures: {
            min: 1,
            inRow: false
        },
        fields: {
            inRow: false
        }
    }
};
